import { useEffect, useRef, useState, type CSSProperties, type MouseEvent, type PointerEvent } from 'react'
import { bridge } from '../bridge'

// iOS-style interactive back: a drag that STARTS at the left edge of a pushed
// screen and moves right slides the screen off, following the finger, and
// reveals the parent underneath. On release, momentum decides: if the screen
// would coast past the halfway point, the pop finishes (at the finger's speed);
// otherwise the screen springs back.
//
// Only a drag that begins in the first EDGE px counts. That is what lets it
// coexist with swipe-right-to-reply on chat bubbles and with sideways-scrolling
// content (code blocks, tables): anything starting further in belongs to the
// content, exactly as in iMessage/WhatsApp.
//
// The handlers run in the CAPTURE phase on the screen container. An edge-started
// pointerdown is stopped there so no child gesture (a bubble's swipe-to-reply or
// long-press) ever begins under it. Clicks are separate events, so a plain tap at
// the edge (e.g. the left side of the Back button) still works.
//
// Touch and pen only: a mouse has no edge-swipe convention, and desktop keeps
// the Back button and the browser back button (see nav/backHistory.ts).

const EDGE = 24 // px from the screen's left edge where a back swipe may start
const SLOP = 10 // px of motion before we decide it's a drag
// The commit rule is momentum projection, as in Apple's "Designing Fluid
// Interfaces": where would the screen come to rest if it kept the finger's
// release velocity and decelerated? Commit if that point is past halfway. One
// rule covers every case: a slow drag must be carried past halfway by hand, a
// moderate swipe gets there with a little momentum, a fast flick from early on
// gets there easily, and a flick back toward the edge (negative velocity)
// cancels even from past halfway, just like iOS.
//
// History: the first cut (35% or a single-sample 0.5 px/ms) popped on almost any
// short swipe. The second required >=1000 px/s AND 20% of the width, which made
// ordinary medium-speed swipes spring back. Projection sits between the two.
const COMMIT_FRACTION = 0.5 // the projected rest point must pass this share of the width
// How far momentum carries the screen, as ms of travel at the release velocity.
// UIKit's normal deceleration (0.998/ms) would be ~500 ms; that is far too eager
// for a back gesture, so this is closer to its "fast" rate.
const PROJECTION_MS = 180
// A real drag, not a twitch: a quick nudge smaller than this share of the width
// never pops, however fast it was.
const MIN_COMMIT_FRACTION = 0.12
// Release velocity is averaged over the samples in this trailing window, not one
// move-to-move step (noisy on a phone). A finger that rested for the whole
// window before lifting has no velocity, so drag-pause-lift is never a flick.
const VELOCITY_WINDOW_MS = 100
const VELOCITY_MIN_SPAN_MS = 20
const SETTLE_MS = 220 // the slowest the release animation runs
const MIN_SETTLE_MS = 90 // …and the fastest, for a hard flick

export function useEdgeSwipeBack(enabled: boolean, onBack: () => void) {
  const [dx, setDx] = useState(0)
  const [dragging, setDragging] = useState(false)
  // True while the release animation (finish or spring-back) runs, so the
  // transition stays applied until it completes instead of snapping.
  const [settling, setSettling] = useState(false)
  // Release animation length: a flick finishes at the finger's speed rather
  // than slowing down into a fixed-length animation.
  const [settleMs, setSettleMs] = useState(SETTLE_MS)
  const pointerId = useRef<number | null>(null)
  const active = useRef(false)
  const startX = useRef(0)
  const startY = useRef(0)
  const width = useRef(0)
  const dxRef = useRef(0)
  const samples = useRef<{ x: number; t: number }[]>([])
  const swallowClick = useRef(false)
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (settleTimer.current) clearTimeout(settleTimer.current)
    },
    [],
  )

  function track(next: number) {
    dxRef.current = next
    setDx(next)
  }

  function onPointerDownCapture(e: PointerEvent<HTMLElement>) {
    if (!enabled || e.pointerType === 'mouse' || pointerId.current !== null) return
    const rect = e.currentTarget.getBoundingClientRect()
    if (e.clientX - rect.left > EDGE) return
    // Ours now: keep children from starting their own gestures under this touch.
    e.stopPropagation()
    pointerId.current = e.pointerId
    active.current = false
    startX.current = e.clientX
    startY.current = e.clientY
    width.current = rect.width || window.innerWidth
    samples.current = [{ x: e.clientX, t: Date.now() }]
  }

  function onPointerMoveCapture(e: PointerEvent<HTMLElement>) {
    if (pointerId.current !== e.pointerId) return
    const deltaX = e.clientX - startX.current
    const deltaY = e.clientY - startY.current
    if (!active.current) {
      // Mostly vertical, or leftward: not a back swipe. Let it go.
      if (Math.abs(deltaY) > SLOP && Math.abs(deltaY) > Math.abs(deltaX)) {
        pointerId.current = null
        return
      }
      if (deltaX < -SLOP) {
        pointerId.current = null
        return
      }
      if (deltaX < SLOP) return
      active.current = true
      setDragging(true)
      e.currentTarget.setPointerCapture?.(e.pointerId)
    }
    e.stopPropagation()
    const now = Date.now()
    samples.current.push({ x: e.clientX, t: now })
    // Keep a little more than the window so the average has a starting point.
    while (samples.current.length > 2 && now - samples.current[1].t > VELOCITY_WINDOW_MS) {
      samples.current.shift()
    }
    track(Math.max(0, deltaX))
  }

  // Rightward speed (px/ms, negative = heading back toward the edge) over the
  // trailing window ending now; 0 if the finger hasn't moved within it or the
  // span is too short to trust.
  function releaseVelocity(now: number): number {
    const recent = samples.current.filter((p) => now - p.t <= VELOCITY_WINDOW_MS)
    if (recent.length < 2) return 0
    const first = recent[0]
    const lastP = recent[recent.length - 1]
    const span = lastP.t - first.t
    if (span < VELOCITY_MIN_SPAN_MS) return 0
    return (lastP.x - first.x) / span
  }

  // pointerup: the finger lifted, so a flick counts.
  function onPointerUpCapture(e: PointerEvent<HTMLElement>) {
    endGesture(e, true)
  }

  // pointercancel: iOS may send this in place of pointerup, and sometimes while
  // the finger is still down. It gets no momentum: it goes back only if the drag
  // was already past the halfway point; otherwise it springs back.
  function onPointerCancelCapture(e: PointerEvent<HTMLElement>) {
    endGesture(e, false)
  }

  // Reads refs, never the render closure (same lesson as SwipeToReply).
  function endGesture(e: PointerEvent<HTMLElement>, withMomentum: boolean) {
    if (pointerId.current !== e.pointerId) return
    pointerId.current = null
    if (!active.current) return
    active.current = false
    e.stopPropagation()
    swallowClick.current = true
    setDragging(false)
    const w = width.current
    const dxNow = dxRef.current
    const v = withMomentum ? releaseVelocity(Date.now()) : 0
    const commit = shouldCommitBack(dxNow, v, w)
    // Finish at the finger's speed when that's quicker than the default.
    const remaining = commit ? w - dxNow : dxNow
    const speed = Math.abs(v)
    const ms = speed > 0 ? Math.min(SETTLE_MS, Math.max(MIN_SETTLE_MS, remaining / speed)) : SETTLE_MS
    setSettleMs(ms)
    setSettling(true)
    if (commit) {
      bridge.call('haptic.light').catch(() => {})
      track(width.current) // animate the rest of the way off-screen
    } else {
      track(0) // spring back
    }
    settleTimer.current = setTimeout(() => {
      settleTimer.current = null
      if (commit) onBack()
      // No-ops once the screen has unmounted; resets it if it's still here.
      track(0)
      setSettling(false)
    }, ms)
  }

  function onClickCapture(e: MouseEvent<HTMLElement>) {
    // A completed or cancelled drag can still produce a click on whatever was
    // under the finger. Don't let it activate anything.
    if (swallowClick.current) {
      swallowClick.current = false
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const style: CSSProperties | undefined =
    dx > 0 || dragging || settling
      ? {
          transform: `translateX(${dx}px)`,
          transition: dragging ? 'none' : `transform ${settleMs}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
          boxShadow: '-6px 0 18px rgba(0, 0, 0, 0.18)',
        }
      : undefined

  return {
    style,
    handlers: {
      onPointerDownCapture,
      onPointerMoveCapture,
      onPointerUpCapture,
      onPointerCancelCapture,
      onClickCapture,
    },
  }
}

// shouldCommitBack is the release decision: dx is how far the screen has been
// dragged, v the release velocity (px/ms; negative = back toward the edge), w
// the screen width. Pure, for testing.
export function shouldCommitBack(dx: number, v: number, w: number): boolean {
  if (dx < w * MIN_COMMIT_FRACTION) return false
  return dx + v * PROJECTION_MS > w * COMMIT_FRACTION
}
